import type { ReactNode } from 'react';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { InfoHint, Th, THead, Tr } from '../../ui/index.ts';

/**
 * Column headers for the merged supply table (#1428).
 *
 * The renames are the point, not decoration. The old table labelled the
 * upstream-channel column 「命名空间」 and the capability's model 「物理模型」,
 * which read as synonyms for the SKU's own namespace and model — the two
 * fields an operator must NOT confuse, because the whole cross-model design
 * hangs on them being different. Each header now carries its definition.
 */

/**
 * Number of columns — group header rows span it.
 *
 * Seven, not nine: weight / retry order / enabled were folded into one
 * 「scheduling」 cell in round 5. As separate columns the table wanted ~1200px
 * of content and, inside the app shell at 1440, crushed both number inputs to
 * 41px of unreadable box — the exact control this whole merge was built to
 * expose.
 */
export const SUPPLY_COLUMN_COUNT = 7;

/**
 * Width ceiling for the three identifier columns — 上游, 上游渠道 and 上游模型
 * (#1442).
 *
 * All three, not just the two named in the report: 上游模型 carries a 36-char
 * `capability_id`, so capping only its neighbours would leave it as the sole
 * id column still sized by its content — the same failure one column over,
 * arriving silently the next time an id gets longer.
 *
 * Their cells already carry `truncate`, which does nothing on its own here:
 * `text-overflow: ellipsis` needs a constrained width, and an auto-layout
 * table cell takes the width its content asks for. On dev, 上游's sub-line
 * (`ups_<uuid> · ua_<12> · ukb_<32>`) asked for 672px, the table grew past
 * its 1155px container, and the four columns that matter — probe,
 * multiplier, scheduling, actions — sat off screen at 1440. The short ids in
 * the component fixtures (`up_oauth`) never triggered it.
 *
 * Applied to a block INSIDE the cell rather than to the cell itself: browsers
 * treat `max-width` on a `<td>` under `table-layout: auto` as advisory, but a
 * block box's max-width does clamp the intrinsic contribution the column is
 * sized from. Full values stay reachable through each line's `title`.
 *
 * A ceiling, not a fixed width — narrower content still collapses the column
 * and gives the space to its neighbours.
 */
export const SUPPLY_ID_CELL_CLASS = 'max-w-[11rem]';

function HintedTh({
  labelKey,
  hintKey,
  num = false,
  testId,
  className,
}: {
  readonly labelKey: I18nKey;
  readonly hintKey: I18nKey;
  readonly num?: boolean;
  readonly testId?: string;
  readonly className?: string;
}): JSX.Element {
  const t = useT();
  return (
    <Th num={num} className={className}>
      <span className="inline-flex items-center gap-1">
        {t(labelKey)}
        <InfoHint label={t('sku_detail.hint.label')} content={t(hintKey)} testId={testId} />
      </span>
    </Th>
  );
}

export function SupplyTableHead(): JSX.Element {
  const t = useT();
  const columns: readonly ReactNode[] = [
    <HintedTh
      key="upstream"
      labelKey="sku_detail.supply.column.upstream"
      hintKey="sku_detail.hint.upstream"
    />,
    <HintedTh
      key="upstream-channel"
      labelKey="sku_detail.supply.column.upstream_channel"
      hintKey="sku_detail.hint.upstream_channel"
      testId="sku-supply-hint-upstream-channel"
    />,
    <HintedTh
      key="upstream-model"
      labelKey="sku_detail.supply.column.upstream_model"
      hintKey="sku_detail.hint.capability"
    />,
    <HintedTh
      key="probe"
      labelKey="sku_detail.supply.column.probe"
      hintKey="sku_detail.hint.probe_status"
    />,
    <HintedTh
      key="multiplier"
      labelKey="sku_detail.supply.column.multiplier"
      hintKey="sku_detail.hint.multiplier"
      className="whitespace-nowrap"
      num
    />,
    // One hint, not one per control. This column merges weight and retry
    // order, and two adjacent question marks give no clue which is which —
    // the combined hint names both, and each control keeps its inline label
    // in the cell below.
    <Th key="scheduling" className="whitespace-nowrap text-right">
      <span className="inline-flex items-center gap-1">
        {t('sku_detail.supply.column.scheduling')}
        <InfoHint
          label={t('sku_detail.hint.label')}
          content={t('sku_detail.hint.scheduling')}
          testId="sku-supply-hint-scheduling"
        />
      </span>
    </Th>,
    <Th key="action" className="text-right">{t('sku_detail.supply.column.action')}</Th>,
  ];
  return (
    <THead>
      <Tr interactive={false}>{columns}</Tr>
    </THead>
  );
}
